import { useTemplates } from '@restaurant/api-client/react';
import { countPlaceholders } from '@restaurant/shared';
import { FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Select, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';

export function TemplateSendDialog({ conversationId, trigger }: { conversationId: string; trigger?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const { data: templates = [] } = useTemplates();
  const approved = templates.filter((t) => t.status === 'APPROVED');
  const [key, setKey] = useState<string>();
  const [params, setParams] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const selected = approved.find((t) => `${t.name}|${t.language}` === key);
  const body = useMemo(
    () => ((selected?.components ?? []) as { type: string; text?: string }[]).find((c) => c.type.toUpperCase() === 'BODY')?.text ?? '',
    [selected],
  );
  const count = countPlaceholders(body);
  const preview = body.replace(/\{\{(\d+)\}\}/g, (_, n: string) => params[Number(n) - 1] || `{{${n}}}`);

  const send = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.conversations.sendTemplate(conversationId, { name: selected.name, language: selected.language, bodyParams: params.slice(0, count), headerParams: [] });
      toast.success('Template queued');
      setOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <FileText /> Template
          </Button>
        )}
      </DialogTrigger>
      <DialogContent title="Send a template" description="Approved templates can be sent even when the 24-hour window is closed.">
        {approved.length === 0 ? (
          <p className="text-sm text-muted-foreground">No approved templates yet. Create one on the Templates page.</p>
        ) : (
          <div className="grid gap-4">
            <Field label="Template">
              <Select
                value={key}
                onValueChange={(v) => {
                  setKey(v);
                  setParams([]);
                }}
                placeholder="Choose a template"
                options={approved.map((t) => ({ value: `${t.name}|${t.language}`, label: `${t.name} (${t.language})` }))}
              />
            </Field>
            {Array.from({ length: count }, (_, i) => (
              <Field key={i} label={`Variable {{${i + 1}}}`}>
                <Input value={params[i] ?? ''} onChange={(e) => setParams((p) => Object.assign([...p], { [i]: e.target.value }))} />
              </Field>
            ))}
            {selected ? <div className="whitespace-pre-wrap rounded-lg bg-bubble-out p-3 text-sm">{preview}</div> : null}
            <Button onClick={() => void send()} disabled={!selected || busy || params.slice(0, count).filter(Boolean).length < count}>
              {busy ? <Spinner /> : null} Send template
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
